import type { User } from "@supabase/supabase-js";
import { posthog, analyticsEnabled } from "./posthog";

/**
 * Burrow's tracking plan — the single, typed source of truth for product analytics.
 *
 * Every analytics event the app emits is declared in `AnalyticsEventMap` below and
 * fired through `track()`. This is how a mature analytics team keeps a product
 * instrumented: one catalog enforces the naming convention (`object_action`,
 * snake_case) and the exact property shape for each event, so dashboards and
 * funnels never break on a typo'd event name or a renamed property.
 *
 * Adding a new event = add a line to the map, then call `track("my_event", {...})`.
 * TypeScript then requires the right properties at every call site.
 */

/** Surfaces an action can originate from — kept as a union so funnels can segment by entry point. */
export type Surface =
  | "quick_capture"
  | "home_inline"
  | "share_target"
  | "onboarding"
  | "community_search"
  | "profile"
  | "edition"
  | "collection"
  | "library"
  | "map"
  | "settings"
  | "nav";

type CardType = "text" | "image" | "link" | "voice";

// Events that carry no properties. `keyof Record<never, never>` is `never`, which
// lets `track()` make the properties argument optional for these below.
type NoProps = Record<never, never>;

export interface AnalyticsEventMap {
  // ── Auth / Onboarding ──────────────────────────────────────────────────────
  signup_started: { has_first_name: boolean; has_last_name: boolean };
  signup_completed: { requires_email_confirmation: boolean; handle?: string };
  signup_failed: { reason: string };
  signin_succeeded: NoProps;
  signin_failed: { reason: string };
  password_reset_requested: NoProps;
  password_reset_code_verified: NoProps;
  password_updated: NoProps;
  signed_out: NoProps;
  onboarding_started: NoProps;
  onboarding_thought_added: { has_image: boolean; char_count: number };
  onboarding_completed: { thought_count: number; image_count: number };
  onboarding_extension_step_shown: NoProps;
  onboarding_extension_install_clicked: NoProps;
  onboarding_extension_skipped: NoProps;

  // ── Capture (cards / thoughts) ─────────────────────────────────────────────
  card_capture_opened: { surface: Surface };
  card_created: {
    card_id: string;
    card_type: CardType;
    surface: Surface;
    has_title: boolean;
    has_body: boolean;
    has_source: boolean;
    char_count?: number;
  };
  card_updated: { card_id: string; fields: string[]; is_content_edit: boolean };
  card_deleted: { card_id: string; was_borrowed: boolean };
  /** Owner pulled a public card out of the commons; it returns as a fresh private card. */
  card_withdrawn_from_commons: { card_id: string; new_card_id: string };
  card_tag_added: { card_id: string; tag: string };
  card_tag_removed: { card_id: string; tag: string };
  voice_capture_completed: { duration_ms?: number; transcript_len: number };

  // ── Collections ────────────────────────────────────────────────────────────
  collection_created: { collection_id: string; is_public: boolean; has_description: boolean; surface?: Surface };
  collection_updated: { collection_id: string; fields: string[] };
  collection_deleted: { collection_id: string; card_count: number };
  collection_visibility_changed: { collection_id: string; is_public: boolean; card_count?: number };
  card_added_to_collection: { collection_id: string; card_id: string; auto_labeled?: boolean };
  cards_bulk_added_to_collection: { collection_id: string; count: number };
  card_removed_from_collection: { collection_id: string; card_id: string };
  collection_link_copied: { collection_id: string };

  // ── Editions / Publishing ──────────────────────────────────────────────────
  edition_created: {
    edition_id: string;
    collection_id: string;
    view_modes: string[];
    mode_count: number;
    has_password: boolean;
    has_cover: boolean;
    thought_count: number;
  };
  edition_publish_failed: { collection_id: string; reason: string };
  edition_deleted: { edition_id: string };
  edition_link_copied: { edition_id: string; surface: Surface };

  // ── Trails / Threads ───────────────────────────────────────────────────────
  thread_created: { thread_id: string; step_count: number };
  thread_updated: { thread_id: string; step_count: number };
  thread_deleted: { thread_id: string };
  trail_published: { thread_id: string; has_password: boolean; is_republish: boolean };
  trail_unpublished: { thread_id: string };
  trail_link_copied: { thread_id: string };

  // ── Community / Borrow (core viral loop) ───────────────────────────────────
  borrow_dialog_opened: { card_id: string; is_logged_in: boolean; surface: Surface };
  card_borrowed: {
    source_card_id: string;
    new_card_id: string;
    source_author_id?: string;
    chain_depth?: number;
    surface: Surface;
  };
  borrow_failed: { source_card_id?: string; reason: string };
  community_card_opened: { card_id: string; surface: Surface; borrow_count?: number };

  // ── Social (follow / profiles) ─────────────────────────────────────────────
  user_followed: { target_user_id: string; surface: Surface };
  user_unfollowed: { target_user_id: string };
  profile_viewed: { profile_user_id: string; is_owner_view: boolean };

  // ── Search ─────────────────────────────────────────────────────────────────
  search_performed: { query_len: number; pane: "user" | "community"; result_count: number };
  search_result_opened: { result_type: string; result_id: string };

  // ── Thinking Space / Mind Map ──────────────────────────────────────────────
  thinking_space_opened: { card_count: number; connection_count: number };
  connection_created: { via: "manual_select" | "suggested" };
  connection_deleted: { connection_id: string };

  // ── Subscription / Paywall (free → paid funnel) ────────────────────────────
  paywall_viewed: { trigger: "route_gate" | "manual"; sub_status: string };
  billing_interval_toggled: { interval: "monthly" | "yearly" };
  checkout_started: { plan?: string; price_id?: string; interval?: "monthly" | "yearly" };
  checkout_redirect_failed: { reason: string };
  subscription_activated: { status: string; price_id: string | null };
  manage_subscription_clicked: { sub_status: string };

  // ── Settings / Privacy ─────────────────────────────────────────────────────
  data_exported: { thought_count: number };
  theme_toggled: { theme: string };
  settings_extension_promo_dismissed: NoProps;
  settings_extension_link_clicked: NoProps;

  // ── Navigation ─────────────────────────────────────────────────────────────
  nav_item_clicked: { destination: string; device: "desktop" | "mobile" };
}

export type AnalyticsEvent = keyof AnalyticsEventMap;

// Make the properties argument optional only for events declared with no props.
type TrackArgs<E extends AnalyticsEvent> = keyof AnalyticsEventMap[E] extends never
  ? [properties?: undefined]
  : [properties: AnalyticsEventMap[E]];

/**
 * Emit a typed product-analytics event. No-ops (with a dev-console echo) when
 * PostHog is not configured, so instrumentation is always safe to call.
 */
export function track<E extends AnalyticsEvent>(event: E, ...args: TrackArgs<E>): void {
  const properties = args[0];
  if (!analyticsEnabled) {
    if (import.meta.env.DEV) console.debug("[analytics]", event, properties ?? "");
    return;
  }
  posthog.capture(event, properties);
}

/** True when the app is running as an installed PWA rather than a browser tab. */
function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/** Properties attached to EVERY event so any funnel can segment by them. */
function superProperties(extra?: Record<string, unknown>): Record<string, unknown> {
  return {
    platform: isStandalone() ? "pwa" : "web",
    ...extra,
  };
}

export interface IdentifyContext {
  handle?: string | null;
  plan?: string | null;
  subscriptionStatus?: string | null;
}

/**
 * Associate the current PostHog + Sentry session with a signed-in user and set
 * the person/super properties every downstream event should be segmentable by.
 */
export function identifyUser(user: User, ctx: IdentifyContext = {}): void {
  if (!analyticsEnabled) return;

  const firstName = (user.user_metadata?.first_name as string | undefined) ?? undefined;
  const lastName = (user.user_metadata?.last_name as string | undefined) ?? undefined;

  posthog.identify(
    user.id,
    {
      // 2nd arg = `$set`: overwritten on every identify.
      email: user.email,
      name: [firstName, lastName].filter(Boolean).join(" ") || undefined,
      handle: ctx.handle ?? undefined,
      plan: ctx.plan ?? undefined,
      subscription_status: ctx.subscriptionStatus ?? undefined,
    },
    {
      // 3rd arg = `$set_once`: recorded only the first time we ever see this user.
      signup_date: user.created_at,
    }
  );

  posthog.register(superProperties({ plan: ctx.plan ?? "free", subscription_status: ctx.subscriptionStatus }));
}

/** Clear identity + super properties on sign-out. */
export function resetUser(): void {
  if (!analyticsEnabled) return;
  posthog.reset();
}

/** Register/refresh super properties without a full re-identify (e.g. plan changed mid-session). */
export function updateSuperProperties(extra: Record<string, unknown>): void {
  if (!analyticsEnabled) return;
  posthog.register(superProperties(extra));
}

// Collapse the dynamic segments of the known routes so pageviews group by
// pattern (`/collections/:id`) instead of exploding on every concrete id/slug.
const ROUTE_PATTERNS: [RegExp, string][] = [
  [/^\/collections\/[^/]+$/, "/collections/:id"],
  [/^\/c\/[^/]+$/, "/c/:slug"],
  [/^\/editions\/[^/]+$/, "/editions/:id"],
  [/^\/trails\/[^/]+$/, "/trails/:slug"],
  [/^\/u\/[^/]+$/, "/u/:handle"],
];

export function normalizeRoute(pathname: string): string {
  for (const [re, pattern] of ROUTE_PATTERNS) {
    if (re.test(pathname)) return pattern;
  }
  return pathname || "/";
}

/**
 * Capture a pageview keyed by the matched route *pattern* (e.g. `/collections/:id`)
 * rather than the concrete URL, so high-cardinality ids don't shatter the report.
 */
export function trackPageview(pathname: string): void {
  if (!analyticsEnabled) return;
  posthog.capture("$pageview", { route: normalizeRoute(pathname), $pathname: pathname });
}
